import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Form from 'flarum/common/components/Form';
import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Select from 'flarum/common/components/Select';
export default class AssignBadgeModal extends Modal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "user", void 0);
    _defineProperty(this, "badges", []);
    _defineProperty(this, "categories", []);
    _defineProperty(this, "userBadgeIds", new Set());
    _defineProperty(this, "loading", true);
    _defineProperty(this, "saving", false);
    _defineProperty(this, "selectedBadgeId", null);
    _defineProperty(this, "reason", '');
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.user = this.attrs.user;
    this.loadBadges();
  }
  async loadBadges() {
    this.loading = true;
    m.redraw();
    try {
      const [badges, categories, userBadges] = await Promise.all([app.store.find('badges', {
        include: 'category'
      }), app.store.find('badge-categories'), app.store.find('user-badges', {
        filter: {
          user: String(this.user.id())
        }
      })]);

      // Get the badge IDs that this user already has
      this.userBadgeIds = new Set(userBadges.filter(ub => ub && typeof ub.badge === 'function' && ub.badge()).map(ub => ub.badge().id()));

      // Filter only active manual badges (no trigger_config or empty conditions)
      // and exclude badges the user already has
      this.badges = badges.filter(b => {
        if (!b || typeof b.id !== 'function' || !b.isActive()) return false;
        // Exclude badges user already has
        if (this.userBadgeIds.has(b.id())) return false;
        const triggerConfig = b.triggerConfig();
        // Manual badge = no trigger config or no conditions
        return !triggerConfig || !triggerConfig.conditions || triggerConfig.conditions.length === 0;
      });
      this.categories = categories.filter(c => c && typeof c.id === 'function' && c.isEnabled());
    } catch (error) {
      console.error('Failed to load badges:', error);
      this.badges = [];
      this.categories = [];
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  className() {
    return 'AssignBadgeModal';
  }
  title() {
    return app.translator.trans('fof-badges.forum.user.assign_badge_title', {
      username: this.user.displayName()
    });
  }
  content() {
    if (this.loading) {
      return m("div", {
        className: "Modal-body"
      }, m("div", {
        className: "AssignBadgeModal-loading"
      }, m(LoadingIndicator, null)));
    }
    if (this.badges.length === 0) {
      return m("div", {
        className: "Modal-body"
      }, m("div", {
        className: "AssignBadgeModal-empty"
      }, m("i", {
        className: "fas fa-award"
      }), m("p", null, app.translator.trans('fof-badges.forum.user.no_badges_available'))));
    }

    // Build badge options grouped by category
    const options = {
      '': app.translator.trans('fof-badges.forum.user.select_badge')
    };

    // Sort badges by category
    const uncategorized = [];
    const categorized = new Map();
    this.badges.forEach(badge => {
      const category = badge.category();
      if (category && typeof category.id === 'function') {
        const catId = category.id();
        if (!categorized.has(catId)) {
          categorized.set(catId, []);
        }
        categorized.get(catId).push(badge);
      } else {
        uncategorized.push(badge);
      }
    });

    // Add categorized badges
    this.categories.forEach(category => {
      const catBadges = categorized.get(category.id());
      if (catBadges && catBadges.length > 0) {
        catBadges.forEach(badge => {
          options[badge.id()] = "".concat(category.name(), " > ").concat(badge.name());
        });
      }
    });

    // Add uncategorized badges
    uncategorized.forEach(badge => {
      options[badge.id()] = badge.name();
    });
    return m("div", {
      className: "Modal-body"
    }, m(Form, null, m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('fof-badges.forum.user.select_badge_label')), m(Select, {
      options: options,
      value: this.selectedBadgeId || '',
      onchange: value => {
        this.selectedBadgeId = value || null;
      },
      className: "FormControl"
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('fof-badges.forum.user.assign_reason')), m("textarea", {
      className: "FormControl",
      value: this.reason,
      oninput: e => {
        this.reason = e.target.value;
      },
      rows: 3,
      placeholder: app.translator.trans('fof-badges.forum.user.assign_reason_placeholder')
    }), m("p", {
      className: "helpText"
    }, app.translator.trans('fof-badges.forum.user.assign_reason_help'))), m("div", {
      className: "Form-group AssignBadgeModal-buttons"
    }, m(Button, {
      className: "Button Button--primary",
      loading: this.saving,
      disabled: !this.selectedBadgeId,
      onclick: () => this.assign()
    }, app.translator.trans('fof-badges.forum.user.assign_button')), m(Button, {
      className: "Button",
      onclick: () => this.hide()
    }, app.translator.trans('fof-badges.forum.user.cancel')))));
  }
  async assign() {
    if (!this.selectedBadgeId) return;
    const selectedBadge = this.badges.find(b => b.id() === this.selectedBadgeId);
    if (!selectedBadge) return;
    const userName = this.user.displayName();
    const badgeName = selectedBadge.name();
    this.saving = true;
    m.redraw();
    try {
      var _this$attrs$onAssign, _this$attrs;
      await app.store.createRecord('user-badges').save({
        userId: this.user.id(),
        badgeId: this.selectedBadgeId,
        reason: this.reason || null
      });
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('fof-badges.forum.user.badge_assigned', {
        badge: badgeName,
        username: userName
      }));
      (_this$attrs$onAssign = (_this$attrs = this.attrs).onAssign) == null || _this$attrs$onAssign.call(_this$attrs);
      this.hide();
    } catch (error) {
      // Error will be handled by Flarum's default error handler
      throw error;
    } finally {
      this.saving = false;
      m.redraw();
    }
  }
}
flarum.reg.add('fof-badges', 'forum/components/AssignBadgeModal', AssignBadgeModal);